/**
 * Ruta Design System — the ONE permitted source for literal color VALUES in
 * the webapp. Everything here is derived from platform standards; this file
 * contains zero hand-written brand hexes.
 *
 * Two upstream sources, no duplication:
 *   1. Tailwind's own color scales (`tailwindcss/colors`) for the four
 *      sanctioned neutral/utility families: stone, green, orange, red.
 *   2. The semantic/brand CSS variables declared in `src/index.css`
 *      (`--primary`, `--nav`, `--success`, …). Those HSL-triplet tokens are
 *      the single source for brand color; we never re-type `#486948` /
 *      `#0a1f0a` here.
 *
 * WHEN TO USE WHAT (in priority order):
 *   - Tailwind utility classes (`bg-primary`, `text-stone-500`) — ALWAYS the
 *     first choice. They already flow from the CSS variables.
 *   - `TOKEN.*` — css-var strings (`hsl(var(--primary))`) for inline
 *     `style={}` on DOM/SVG elements and Mapbox popup HTML, where `var()`
 *     resolves normally.
 *   - The scale re-exports (`STONE`, `GREEN`, `ORANGE`, `RED`) — concrete
 *     hexes for contexts CSS classes can't reach: Mapbox GL paint (WebGL),
 *     recharts props, jsPDF, SVG data-URIs.
 *   - `resolveCssColor(varName)` — the runtime resolver. For the SAME
 *     unreachable contexts, but when the value is a SEMANTIC TOKEN (brand
 *     primary, nav/forest). Renders the token through the DOM so `var()` +
 *     `hsl()` collapse to a concrete `rgb(...)` string. WebGL/canvas/jsPDF
 *     cannot resolve `var()` themselves.
 *
 * INVARIANT — pins == legends: `customerStatusPinColors()` is the canonical
 * customer-status color set. Every map paint expression AND every legend
 * component must read from it, so a pin can never disagree with its legend.
 */

import colors from 'tailwindcss/colors';

/* ---------- (1) Sanctioned Tailwind scales ----------------------------- *
 * Plain nested objects re-exported straight from Tailwind. v3's deprecated
 * color getters (lightBlue/warmGray/…) only warn when *accessed*; we touch
 * only these four families, so importing the module is inert and cheap. */
export const STONE = colors.stone;
export const GREEN = colors.green;
export const ORANGE = colors.orange;
export const RED = colors.red;
export const AMBER = colors.amber;
export const BLUE = colors.blue;

/* Vibrant Tailwind scales for the high-contrast crew-trail palette (memberTrailColor.ts). */
export const VIOLET = colors.violet;
export const CYAN = colors.cyan;
export const LIME = colors.lime;
export const FUCHSIA = colors.fuchsia;
export const PINK = colors.pink;
export const SKY = colors.sky;
export const YELLOW = colors.yellow;

/* ---------- (2a) Semantic tokens as css-var strings (DOM/SVG) ---------- */
export const TOKEN = {
  primary: 'hsl(var(--primary))',
  primaryHover: 'hsl(var(--primary-hover))',
  primaryTint: 'hsl(var(--primary-tint))',
  primaryForeground: 'hsl(var(--primary-foreground))',
  nav: 'hsl(var(--nav))',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  warningSubtle: 'hsl(var(--warning-subtle))',
  warningBorder: 'hsl(var(--warning-border))',
  warningStrong: 'hsl(var(--warning-strong))',
  destructive: 'hsl(var(--destructive))',
  foreground: 'hsl(var(--foreground))',
  background: 'hsl(var(--background))',
  muted: 'hsl(var(--muted))',
  mutedForeground: 'hsl(var(--muted-foreground))',
  border: 'hsl(var(--border))',
  ring: 'hsl(var(--ring))',
} as const;

/* ---------- (2b) Runtime resolver for non-CSS contexts ----------------- */

const _resolvedColorCache = new Map<string, string>();

/**
 * Resolve one of the design-system HSL-triplet CSS variables (e.g. `--primary`,
 * `--nav`) to a concrete `rgb(...)` string, for contexts that genuinely cannot
 * evaluate `var()`: Mapbox GL WebGL paint, canvas, jsPDF.
 *
 * Resolved lazily at call time (never at module load — the stylesheet may not
 * be parsed yet on import) and memoized. Guarded for non-DOM environments
 * (SSR / tests / workers), where it returns the still-valid css-var expression
 * and skips caching so a later DOM-backed call resolves properly.
 */
export function resolveCssColor(varName: string): string {
  const cached = _resolvedColorCache.get(varName);
  if (cached !== undefined) return cached;

  const expr = `hsl(var(${varName}))`;

  if (typeof document === 'undefined' || !document.body) {
    return expr;
  }

  // Render the token through a throwaway element so the browser collapses
  // `var()` + `hsl()` into a concrete `rgb(...)` value we can read back.
  const probe = document.createElement('span');
  probe.style.color = expr;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color || expr;
  probe.remove();

  _resolvedColorCache.set(varName, resolved);
  return resolved;
}

const _resolvedHexCache = new Map<string, string>();

/**
 * Like `resolveCssColor`, but returns a concrete lowercase `#rrggbb` string
 * instead of `rgb(...)`. For the handful of unreachable contexts that need a
 * hex LITERAL specifically: hex-alpha concatenation (`` `${hex}15` ``), jsPDF
 * `setTextColor`, and `<input type="color">` values.
 *
 * Same lazy/memoized/non-DOM-guarded behavior as `resolveCssColor`. In non-DOM
 * environments (SSR / tests / workers) there is no `rgb(...)` to convert, so —
 * matching `resolveCssColor` — this returns the still-valid css-var expression
 * (`hsl(var(--x))`) UNCACHED rather than a hex sentinel, so a later DOM-backed
 * call resolves to a real hex. Every caller runs only in the browser, so the
 * fallback never reaches a paint path.
 */
export function resolveCssColorHex(varName: string): string {
  const cached = _resolvedHexCache.get(varName);
  if (cached !== undefined) return cached;

  const resolved = resolveCssColor(varName);

  // Non-DOM fallback: `resolveCssColor` handed back the css-var expression, not
  // an `rgb(...)` we can parse. Pass it through unchanged and skip caching.
  const match = resolved.match(/rgba?\(([^)]+)\)/i);
  if (!match) return resolved;

  const [r, g, b] = match[1]
    .split(',')
    .slice(0, 3)
    .map((c) => Math.max(0, Math.min(255, Math.round(parseFloat(c)))));

  const hex = `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
  _resolvedHexCache.set(varName, hex);
  return hex;
}

/* ---------- (3) Composite app-semantic sets ---------------------------- *
 * Composed exclusively from (1) scales and (2) tokens — no literal hexes. */

export interface CustomerStatusPinColors {
  prospect: string;
  active: string;
  contacted: string;
  quoted: string;
  inactive: string;
  suspended: string;
  pending: string;
  out_of_service_area: string;
  // Setup-readiness states (derived for the map from services + rates)
  unconfigured: string;
  partially_configured: string;
  fully_configured: string;
  /** unknown / unset status */
  fallback: string;
}

/**
 * THE canonical customer-status color set (pins == legends invariant).
 *
 * A function (not a const) because `contacted`/`quoted` are semantic brand
 * tokens that must be resolved through the DOM for WebGL map paint — call it
 * at render/map-init time, when the stylesheet is live. `active`/`prospect`/…
 * come straight from the sanctioned Tailwind scales.
 */
export function customerStatusPinColors(): CustomerStatusPinColors {
  return {
    prospect: ORANGE[400],
    active: GREEN[600],
    contacted: resolveCssColor('--primary'),
    quoted: resolveCssColor('--nav'),
    inactive: STONE[500],
    suspended: RED[600],
    pending: STONE[600],
    out_of_service_area: ORANGE[600],
    // Setup-readiness ramp: empty → in-progress → ready → live(green, == active)
    unconfigured: STONE[300],
    partially_configured: AMBER[500],
    fully_configured: BLUE[500],
    fallback: STONE[700],
  };
}

/**
 * Categorical chart palette (revenue / daily-performance category series).
 * Index-stable — order is load-bearing so existing charts keep their per-series
 * colors. A function so the `primary` entry resolves to a concrete `rgb(...)`
 * (recharts fills in this codebase are always concrete values, never `var()`
 * strings); every other entry is a Tailwind scale hex. rgb(72,105,72) === the
 * former `#486948`, so rendering is unchanged.
 */
export function chartCategoryColors(): string[] {
  return [
    resolveCssColor('--primary'), // was #486948
    ORANGE[600],
    STONE[400],
    GREEN[600],
    ORANGE[300],
    STONE[600],
    GREEN[300],
    ORANGE[700],
  ];
}

/**
 * Distinguishable multi-series palette (crew/member trails & similar
 * categorical data). Index-stable; concrete values (same rationale as
 * `chartCategoryColors`).
 */
export function seriesColors(): string[] {
  return [
    GREEN[600],
    resolveCssColor('--primary'), // was #486948
    ORANGE[500],
    RED[600],
    ORANGE[300],
    STONE[600],
    GREEN[400],
    RED[800],
  ];
}
